import React, { useState, useEffect } from "react";

import Box from '@mui/material/Box';

import "../App.css"
import {PageHeader} from "./PageHeader";
import {useAppCtx} from "../app/AppCtx";
import {useCrewCtx} from "../app/CrewCtx";
import {useCodeCtx} from "../app/CodeCtx";
import {accessCtrl} from "../ctx/AccessCtrl";
import {CxDataGrid} from "../cmx/CxData";
import {CxGrid} from "../cmx/CxGrid";
import {
    CxResetSearchButton,
    CxDatePicker,
    CxLocalization, CxComboxSelection, CxRadioboxSelection
} from "../cmx/CxForm";
import {CxFormFeedback, CxSnack} from "../cmx/CxFeedback";
import {CxSubtitle, CxText} from "../cmx/CxTypography"
import {CxReportDialog} from "../cmx/CxReport";
import {Task} from "../domain/Task";
import {Report} from "../domain/Report";
import {System} from "../domain/System";
import {initialCall, runningCall, successCall} from "../gateway/CallService";
import {taskSearchList} from "../gateway/TaskService"
import {DateUtil} from "../lib/DateUtil";
import {ValidationUtil} from "../lib/ValidationUtil";

// --------------------------------------------------------------------------------------------------------------------
// Internal Site Domain
// --------------------------------------------------------------------------------------------------------------------

const Site = {

    getSearchValues() {
        return {
            "start_date"                : null,
            "end_date"                  : null,
            "foreman_crew_id"           : -1,
            "deputy_foreman_crew_id"    : -1,
            "prep"                      : -1
        }
    },

    getSearchPrepItems() {
        return [
            {label: "Alle", value :-1},
            {label: "Ja",   value :1},
            {label: "Nein", value :0}
        ]
    },

    getSearchPrepLabel(value) {
        const item = Site.getSearchPrepItems().find(item => item.value === value);
        return item ? item.label : "";
    },

    getSearchErrors() {
        return System.initializeFormErrors(Site.getSearchValues());
    },

    getSearchMinimalLength() {
        return {
        }
    },

    mapSearchToQuery(searchValues) {
        return {
            "start_date"            : System.mapValueToQuery(searchValues.start_date),
            "end_date"              : System.mapValueToQuery(searchValues.end_date),
            "foreman_crew_id"       : System.mapCodeToQuery(searchValues.foreman_crew_id),
            "deputy_foreman_crew_id": System.mapCodeToQuery(searchValues.deputy_foreman_crew_id),
            "prep"                  : System.mapCodeToQuery(searchValues.prep),
        }
    },

    mapListColumns(rows, code, crew) {
        return Task.mapListColumns(rows, code, crew);
    },

    getListColumns() {
        return Task.getListSiteColumns();
    }
}

// --------------------------------------------------------------------------------------------------------------------
// Internal Search Component
// --------------------------------------------------------------------------------------------------------------------


function Search({type}) {

    const {handleAction, getPageData, setPageData, getPageState, setPageState} = useAppCtx();
    const {crew, lazyloadCrews} = useCrewCtx();
    const {code, lazyloadCodes} = useCodeCtx()

    const pageId = 'SiteSearch';
    const viewKey = 'view';

    useEffect(() => {
        lazyloadCrews();
        lazyloadCodes();
    }, []);

    // ----------------------------------------------------------------------------------------------------------------
    // header and view handling
    // ----------------------------------------------------------------------------------------------------------------

    const typeCfg = {
        site: {
            title       : "Baustelle Vorbereitung/Stellvertretung suchen",
            report      : Report.SITE,
            reportTitle : "Baustelle Vorbereitung/Stellvertretung",
            desc        : [
                            "Die Suche liefert alle Baustellen mit geplanten Arbeitsvorgängen im gewählten Zeitraum für die selektierten Bauführer/Stellvertreter.",
                            "Beim Suchfeld 'Vorbereitet' bedeutet: Ja=Alle Vorarbeiten sind erledigt. Nein=Mindestens eine Vorarbeit ist noch offen.",
                            "Beachte: Die Suche ist auf 500 Arbeitsvorgänge limitiert, woraus sich die angezeigte Baustellenliste (1 Zeile pro Baustelle) ergibt."
                          ],
            dataKey     : "siteSearchCriteria",
            returnTo    : "siteSearch",
            isCurrent   : true
        }
    };

    const cfg = typeCfg[type];

    const handleLocalActionChange = (value) => {
        if (value === "openReport") {
            setReport(true);
        }
    };

    const localActionItems = [
        { kind: "tooltip", label: "Report erstellen", iconName: "report", actionName: "openReport" }
    ];

    const actionItems = [
        { kind: "tooltip", label: "Zurück zu Projekte", iconName: "project", actionName: "project" },
        { kind: "tooltip", label: "Zurück zu Planung",  iconName: "plan",    actionName: "task" }
    ];

    const renderHeader = () => {
        const descriptions = Array.isArray(cfg.desc) ? cfg.desc : [cfg.desc];
        return (
            <>
                <PageHeader
                    title={cfg.title}
                    localActionItems={localActionItems}
                    localActionOnChangeHandler={handleLocalActionChange}
                    actionItems={actionItems}
                />

                <CxGrid size="xl">
                    {descriptions.map((text, index) => (
                        <CxText key={index}>{text}</CxText>
                    ))}
                </CxGrid>
            </>
        );
    }

    // ----------------------------------------------------------------------------------------------------------------
    // control panel
    // ----------------------------------------------------------------------------------------------------------------

    const dataKey = cfg.dataKey

    const [formValues, setFormValues] = useState( getPageData(pageId, dataKey, Site.getSearchValues()) );
    const [formErrors, setFormErrors] = useState( Site.getSearchErrors() );
    const [call, setCall] = useState(initialCall());
    const [snack, setSnack] = useState(null);
    const [report, setReport] = useState(false);
    const [sortModel, setSortModel] = useState([])

    //
    // handers
    //
    const handleDateChange = (fieldOrEvent, value) => {
        setFormValues((prev) => ({ ...prev, [fieldOrEvent]: DateUtil.convertDateToIsoString(value) }));
        setFormErrors((prev) => ({ ...prev, [fieldOrEvent]: false }));
    };

    const handleComboxChange = (field, value) => {
        setFormValues((prevValues) => ({
            ...prevValues,
            [field]: value
        }));
    }

    const handlePrepSourceChange = (value) => {
        setFormValues((prevValues) => ({
            ...prevValues,
            prep: value,
        }));
        formErrors.prep = "";
    }

    const handleSubmit = async (action) => {

        if (action === "reset") {
            setPageData(pageId, dataKey, Site.getSearchValues() );
            setFormValues( Site.getSearchValues() )
            setFormErrors( Site.getSearchErrors() )
            setCall(initialCall());
            return
        }

        if (action === "search") {
            // validate
            const errorsCopy = { ...formErrors }; // Create a copy of the errors object
            const isValid = validate(formValues, errorsCopy);
            if (!isValid) {
                setFormErrors(errorsCopy); // Update errors in state
                return
            }
            // save search data
            setPageData(pageId, dataKey, formValues);
            // reset sort model
            setSortModel([])
            // execute search
            search( Site.mapSearchToQuery(formValues) );
        }
    };

    //
    // validation
    //
    function validate(formValues, formErrors) {
        let isValid = true;

        // remove leading and trailing blanks on all input fields
        ValidationUtil.trimTextFields(formValues)

        // check if all search fields are empty
        if ( ValidationUtil.areEqual(formValues, Site.getSearchValues()) ) {
            const snack= `Bitte mindestens 1 Suchkriterium eingeben.`;
            setSnack(snack)
            return false;
        }

        // validate minimal length (if available)
        isValid = ValidationUtil.minLength(formValues, formErrors, Site.getSearchMinimalLength());

        // validate date range
        if (! ValidationUtil.dateRange(formValues, formErrors, 'start_date', 'end_date') ){
            isValid = false;
        }

        // return final validation status
        return isValid;
    }

    //
    // form inputs
    //
    const formDatePicker = (field, label) => {
        return <CxDatePicker field={field} label={label} disablePast={false}
                             handleChange={handleDateChange} formValues={formValues} formErrors={formErrors} />
    }

    const formCombox = (field, label, items) => {
        return <CxComboxSelection id={field} name={label} value={formValues[field]}
                                  onChange={(value) => handleComboxChange(field, value)}
                                  items={items} formErrors={formErrors} />
    }

    const formPrepSelection = () => {
        return <CxRadioboxSelection
            key={formValues['prep']}
            name='prep'
            label="Vorbereitet"
            value={formValues['prep']}
            items={Site.getSearchPrepItems()}
            onChange={handlePrepSourceChange}  />
    }

    const formResetSearchButton = () => {
        return <CxResetSearchButton handleSubmit={handleSubmit} />
    }

    const renderCtrl = () => {
        return (
            <CxLocalization>

                <CxGrid size="lg">{ formDatePicker("start_date", "Startdatum ab")}</CxGrid>
                <CxGrid size="lg">{ formDatePicker("end_date", "Enddatum bis")}</CxGrid>

                <CxGrid size="lg">{ formCombox("foreman_crew_id", "Bauführer", crew.getForemanSelection()) }</CxGrid>
                <CxGrid size="lg">{ formCombox("deputy_foreman_crew_id", "Bauführer Stellvertreter", crew.getForemanSelection()) }</CxGrid>

                <CxGrid size="lg">{ formPrepSelection() }</CxGrid>


                <CxGrid size="lg" justifyContent="flex-end">{ formResetSearchButton() }</CxGrid>
                <CxSnack message={snack} handleClose={() => setSnack(null)} position="topCenter" />

            </CxLocalization>
        );
    }

    // ----------------------------------------------------------------------------------------------------------------
    // select view data
    // ----------------------------------------------------------------------------------------------------------------

    function search(queryParams) {
        setCall(runningCall())
        taskSearchList(type, queryParams, cfg.isCurrent).then(responseCall => {
            if (responseCall.isSuccess) {

                // seach delivers one hit per task in the date range!
                const mappedRows = Site.mapListColumns(responseCall.result, code, crew);

                // filter duplicat projects (if this is done, only project fields should be displayed)
                const seenProjects = new Set();
                const uniqueRows = mappedRows.filter(row => {
                    if (seenProjects.has(row.project_id)) {
                        return false;
                    }
                    seenProjects.add(row.project_id);
                    return true;
                });

                setCall(successCall(uniqueRows));

            } else {
                setCall(responseCall);
            }
        });
    }

    // ----------------------------------------------------------------------------------------------------------------
    // render report
    // ----------------------------------------------------------------------------------------------------------------

    const reportInfo = () => {
        const numPrep = Number(formValues.prep)
        const parts = [];

        if (formValues.start_date)                      parts.push(`Startdatum ab: ${DateUtil.convertIsoStringToString(formValues.start_date)}`);
        if (formValues.end_date)                        parts.push(`Enddatum bis: ${DateUtil.convertIsoStringToString(formValues.end_date)}`);
        if (formValues.foreman_crew_id !== -1)          parts.push(`Bauführer: ${ crew.getName(formValues.foreman_crew_id)}`);
        if (formValues.deputy_foreman_crew_id !== -1)   parts.push(`Bauführer Stellvertreter: ${ crew.getName(formValues.deputy_foreman_crew_id)}`);
        if (numPrep !== -1)                             parts.push(`Vorbereitet: ${ Site.getSearchPrepLabel(numPrep)}`);

        return parts.length > 0 ? parts.join(', ') : null;
    }

    const reportSortedRows = () => {
        const rows = call.isSuccess ? [...call.result] : [];
        return Report.sortByGridSortModel(rows, sortModel);
    }

    const renderReport = () => {
        return (
            <CxReportDialog
                reportType={cfg.report}
                title={cfg.reportTitle}
                rows={reportSortedRows()}
                open={report}
                onClose={() => setReport(false)}
                info={reportInfo()}
            />
        )
    }

    // ----------------------------------------------------------------------------------------------------------------
    // render view
    // ----------------------------------------------------------------------------------------------------------------

    const handleRowDoubleClick = (row) => {
        handleAction("projectDetailFromTask", row, cfg.returnTo);
    };

    const handleEditClick = (row) => {
        const projectData = Task.extractProjectData(row)
        handleAction("projectUpdate", projectData, cfg.returnTo);
    }

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>

                { renderHeader() }
                { renderCtrl() }

                <CxFormFeedback call={call} />

                { call.isSuccess &&
                    <>
                        <CxSubtitle>Resultate</CxSubtitle>

                        <CxGrid size="xl">
                            <CxDataGrid
                                rows={call.result}
                                columns={Site.getListColumns(false, cfg.isWarranty)}
                                onRowDoubleClick={(params) => handleRowDoubleClick(params.row)}
                                onViewAction={(params) => handleRowDoubleClick(params.row)}
                                onEditAction={cfg.isCurrent && accessCtrl.canManageAllTask ? (params) => handleEditClick(params.row) : undefined}
                                onSortModelChange={(model) => setSortModel(model)}
                                noRecordsMessage="Keine Suchresultate gefunden."
                            />
                        </CxGrid>
                    </>
                }

                { report && renderReport() }

            </CxGrid>
        </Box>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// External SiteSearch Components
// --------------------------------------------------------------------------------------------------------------------

export function SiteSearch() {
    return <Search type="site" />
}

// --------------------------------------------------------------------------------------------------------------------
// The end.